import { env } from '@/lib/env'
import { isGatewayStatus, noteConnectionFailure, sendConnectionReports } from './connection-reports'
import { ApiError, type ApiErrorBody } from './errors'

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptions {
  method?: Method
  body?: unknown
  keepalive?: boolean
  signal?: AbortSignal
  query?: Record<string, string | number | undefined>
}

function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

let csrfRequest: Promise<void> | null = null

/** Sanctum SPA auth: obtain the CSRF cookie (XSRF-TOKEN; staging's has its own name) before changes. */
async function ensureCsrfCookie(force = false): Promise<void> {
  if (!force && readCookie(env.xsrfCookie)) return
  csrfRequest ??= fetch(`${env.apiOrigin}/sanctum/csrf-cookie`, { credentials: 'include' })
    .then(() => undefined)
    .finally(() => {
      csrfRequest = null
    })
  await csrfRequest
}

/** The path the API saw, for connection reports (no query string). */
const apiPath = (path: string) => new URL(`${env.apiUrl}${path}`).pathname

/** A gateway's error page is HTML, not the API's JSON: then there's no body to read. */
function parseJson(text: string, ok: boolean): unknown {
  if (!text) return undefined
  if (ok) return JSON.parse(text) as unknown
  try {
    return JSON.parse(text) as unknown
  } catch {
    return undefined
  }
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${env.apiUrl}${path}`)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value))
  }
  return url.toString()
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  }
  const isForm = options.body instanceof FormData
  // The browser sets the multipart boundary itself for FormData.
  if (options.body !== undefined && !isForm) headers['Content-Type'] = 'application/json'
  const xsrf = readCookie(env.xsrfCookie)
  if (xsrf) headers['X-XSRF-TOKEN'] = xsrf

  return fetch(buildUrl(path, options.query), {
    method,
    headers,
    credentials: 'include',
    body:
      options.body === undefined
        ? undefined
        : isForm
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal,
    ...(options.keepalive ? { keepalive: true } : {}),
  })
}

/**
 * The only way the SPA talks to the backend. Laravel is authoritative for
 * auth, permissions and plan limits; this client just transports JSON and
 * turns error envelopes into typed ApiErrors.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  if (method !== 'GET') await ensureCsrfCookie()

  let response: Response
  try {
    response = await send(path, options)
    // CSRF token rotated or session expired: refresh the cookie and retry once.
    if (response.status === 419 && method !== 'GET') {
      await ensureCsrfCookie(true)
      response = await send(path, options)
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    noteConnectionFailure({ kind: 'network', method, path: apiPath(path) })
    throw new ApiError(0, { message: 'Cannot reach Arva SEO. Check your connection and try again.' })
  }

  // Cloudflare's or the server's own error page, not the API answering with its JSON.
  if (isGatewayStatus(response.status) && !response.headers.get('Content-Type')?.includes('json')) {
    noteConnectionFailure({ kind: 'gateway', status: response.status, method, path: apiPath(path) })
  } else {
    sendConnectionReports()
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const json = parseJson(text, response.ok)

  if (!response.ok) {
    const body: ApiErrorBody = typeof json === 'object' && json !== null ? json : {}
    throw new ApiError(response.status, body)
  }

  return json as T
}

/**
 * Download a file the API streams (e.g. a CSV export) with the session cookie,
 * then hand it to the browser. Errors arrive as the usual JSON envelope.
 */
const extensions: [RegExp, string][] = [
  [/application\/pdf/, 'pdf'],
  [/spreadsheetml/, 'xlsx'],
  [/text\/csv/, 'csv'],
]

/**
 * The file name from Content-Disposition (RFC 6266, including filename*=UTF-8''…),
 * or one with the extension the Content-Type calls for. Never guess a type the
 * file isn't: a PDF saved as .csv won't open.
 */
export function downloadName(disposition: string | null, contentType: string | null): string {
  const encoded = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(disposition ?? '')?.[1]
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim())
    } catch {
      /* fall through to the plain name */
    }
  }
  const plain = /filename="?([^";]+)"?/.exec(disposition ?? '')?.[1]?.trim()
  if (plain) return plain
  const ext = extensions.find(([type]) => type.test(contentType ?? ''))?.[1]
  return ext ? `arvaseo-export.${ext}` : 'arvaseo-export'
}

export async function downloadFile(path: string, query?: RequestOptions['query']): Promise<void> {
  let response: Response
  try {
    response = await send(path, { query })
  } catch {
    throw new ApiError(0, { message: 'Cannot reach Arva SEO. Check your connection and try again.' })
  }
  if (!response.ok) {
    const text = await response.text()
    let body: ApiErrorBody
    try {
      body = (text ? JSON.parse(text) : {}) as ApiErrorBody
    } catch {
      body = {}
    }
    throw new ApiError(response.status, body)
  }
  const filename = downloadName(
    response.headers.get('Content-Disposition'),
    response.headers.get('Content-Type'),
  )
  const url = URL.createObjectURL(await response.blob())
  const link = Object.assign(document.createElement('a'), { href: url, download: filename })
  document.body.append(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const http = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    api<T>(path, { query, signal }),
  post: <T = undefined>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body ?? {} }),
  upload: <T = undefined>(path: string, form: FormData) => api<T>(path, { method: 'POST', body: form }),
  put: <T = undefined>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body: body ?? {} }),
  patch: <T = undefined>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body: body ?? {} }),
  delete: <T = undefined>(path: string) => api<T>(path, { method: 'DELETE' }),
}
